import React, { useState, useMemo, useEffect } from "react";
import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import BackBtn from "../../widgets/BackBtn";
import { getErrorResponse } from "../../utils/util";
import {
  Alert,
  Box,
  Container,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Snackbar,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import LinkIcon from "@mui/icons-material/Link";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/utils2";
import { useParams } from "react-router-dom";

// Utility to generate clean URLs from text WITHOUT the leading slash
const generateUrlPath = (text) => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
};

const RegistrationAnalyticsTracker = () => {
  const { type } = useParams();

  // 1. Fetch form data
  const {
    data: formData,
    isLoading: isFormLoading,
    isError: isFormError,
    error: formError,
  } = useApiEp(`/event/get-form-visitor?type=${encodeURIComponent(type)}`);

  // 2. Fetch currently saved tracking settings
  const {
    data: savedSettings,
    isLoading: isSettingsLoading,
    isError: isSettingsError,
  } = useApiEp(`/event/get-tracking-urls?type=${encodeURIComponent(type)}`);

  const [selectedFieldId, setSelectedFieldId] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Extract Select fields safely
  const selectFields = useMemo(() => {
    if (!formData?.form) return [];
    return formData.form.filter((item) => item.type === "Select");
  }, [formData]);

  // Pre-populate dropdown if settings already exist in the database
  useEffect(() => {
    if (savedSettings?.data?.visitorRegTrackingKey) {
      setSelectedFieldId(savedSettings.data.visitorRegTrackingKey);
    }
  }, [savedSettings]);

  // Get the active field object to render the table
  const activeField = useMemo(() => {
    return selectFields.find((f) => f.id === selectedFieldId);
  }, [selectedFieldId, selectFields]);

  // Base visitor registration link returned by the backend
  const visitorLink = savedSettings?.data?.visitorLink || "";

  // Full trackable links = visitorLink + ?tr=<generated path>
  const trackingLinks = useMemo(() => {
    if (!activeField || !visitorLink) return [];
    return activeField.properties.options.map((option) => {
      const path = generateUrlPath(option);
      return {
        option,
        path,
        fullLink: `${visitorLink}?tr=${path}`,
      };
    });
  }, [activeField, visitorLink]);

  const handleSelectChange = (event) => {
    setSelectedFieldId(event.target.value);
  };

  const handleCopyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      setToast({
        open: true,
        message: "Link copied to clipboard!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error copying link:", error);
      setToast({
        open: true,
        message: "Failed to copy link.",
        severity: "error",
      });
    }
  };

  const handleSubmit = async () => {
    if (!activeField) return;

    // Build the payload exactly as the backend expects it
    const visitorRegTrackingUrls = activeField.properties.options.map((opt) =>
      generateUrlPath(opt)
    );

    const payload = {
      visitorRegTrackingKey: activeField.id,
      visitorRegTrackingUrls: visitorRegTrackingUrls,
    };

    setIsSaving(true);
    try {
      await axios.post(
        `${API_ENDPOINT}/event/save-tracking-urls?type=${encodeURIComponent(
          type
        )}`,
        payload,
        getHeader()
      );

      setToast({
        open: true,
        message: "Tracking configuration saved successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error saving tracking configuration:", error);
      // Better error surfacing if backend returns 400/404
      const errorMessage =
        error.response?.data?.error || "Failed to save configuration.";
      setToast({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isFormLoading || isSettingsLoading) {
    return <MuPb />;
  }

  if (isFormError) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {getErrorResponse(formError)}
      </Alert>
    );
  }

  return (
    <Container sx={{ mt: 3, mb: 6 }}>
      <BackBtn />

      {isSettingsError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Could not load previous settings. You can still set up a new
          configuration.
        </Alert>
      )}

      <Box sx={{ mt: 2 }}>
        <Card elevation={3} sx={{ borderRadius: 2 }}>
          <CardHeader
            title="Source Tracking Setup"
            subheader="Select a dropdown field to map its options to trackable URL parameters."
            titleTypographyProps={{ variant: "h5", fontWeight: "bold" }}
          />
          <Divider />

          <CardContent
            sx={{ display: "flex", flexDirection: "column", gap: 4 }}
          >
            {/* Field Selection */}
            <FormControl fullWidth variant="outlined">
              <InputLabel id="tracking-field-select-label">
                Form Field Source
              </InputLabel>
              <MuiSelect
                labelId="tracking-field-select-label"
                value={selectedFieldId}
                onChange={handleSelectChange}
                label="Form Field Source"
              >
                {selectFields.map((field) => (
                  <MenuItem key={field.id} value={field.id}>
                    {field.properties.label}
                  </MenuItem>
                ))}
              </MuiSelect>
            </FormControl>

            {/* Generated URLs Preview */}
            {activeField ? (
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Generated URL Paths for: {activeField.properties.label}
                </Typography>

                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                >
                  <Table size="small" aria-label="generated tracking urls">
                    <TableHead sx={{ backgroundColor: "action.hover" }}>
                      <TableRow>
                        <TableCell>
                          <strong>Form Option</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Generated Path</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeField.properties.options.map((option, index) => {
                        const path = generateUrlPath(option);
                        return (
                          <TableRow key={index} hover>
                            <TableCell>{option}</TableCell>
                            <TableCell>
                              <Chip
                                icon={<LinkIcon fontSize="small" />}
                                label={`/${path}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ fontFamily: "monospace" }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box
                  sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<SaveIcon />}
                    onClick={handleSubmit}
                    disabled={isSaving}
                    disableElevation
                    sx={{ textTransform: "none", px: 4 }}
                  >
                    {isSaving ? "Saving..." : "Confirm & Save URLs"}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  py: 4,
                  textAlign: "center",
                  color: "text.secondary",
                  backgroundColor: "action.hover",
                  borderRadius: 2,
                }}
              >
                <Typography variant="body1">
                  Please select a form field above to view and generate tracking
                  URLs.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Links to use for tracking */}
      {activeField && (
        <Box sx={{ mt: 3 }}>
          <Card elevation={3} sx={{ borderRadius: 2 }}>
            <CardHeader
              title="Links to use for tracking"
              subheader={
                visitorLink
                  ? "Share these full links with each source to track registrations."
                  : "Visitor registration link not available yet. Save the configuration or reload to fetch it."
              }
              titleTypographyProps={{ variant: "h5", fontWeight: "bold" }}
            />
            <Divider />

            <CardContent>
              {visitorLink && trackingLinks.length > 0 ? (
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                >
                  <Table size="small" aria-label="full tracking links">
                    <TableHead sx={{ backgroundColor: "action.hover" }}>
                      <TableRow>
                        <TableCell>
                          <strong>Form Option</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Link to Use</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Copy</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {trackingLinks.map(({ option, fullLink }, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{option}</TableCell>
                          <TableCell>
                            <Typography
                              component="a"
                              href={fullLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.85rem",
                                wordBreak: "break-all",
                                color: "primary.main",
                                textDecoration: "none",
                                "&:hover": { textDecoration: "underline" },
                              }}
                            >
                              {fullLink}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Copy link">
                              <IconButton
                                size="small"
                                onClick={() => handleCopyLink(fullLink)}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box
                  sx={{
                    py: 4,
                    textAlign: "center",
                    color: "text.secondary",
                    backgroundColor: "action.hover",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body1">
                    No visitor link available yet to build full tracking links.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity} sx={{ width: "100%", boxShadow: 3 }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default RegistrationAnalyticsTracker;
